import { MessagingGetMessagingConfigResponse } from '@oystehr/sdk';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { QuickText, UnreadMessagesOutput, useErrorQuery, useSuccessQuery } from 'utils';
import { ConversationMessage } from 'utils';
import { useApiClients } from '../../hooks/useAppClients';
import { MessageModel } from './ChatModal';

const normalizeErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    const maybePayload = error as Error & { cause?: unknown; response?: any };
    const details = maybePayload.response?.output || maybePayload.cause;
    if (details) {
      try {
        return `${error.message} - ${typeof details === 'string' ? details : JSON.stringify(details)}`;
      } catch {
        return error.message;
      }
    }
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return 'Error desconocido';
  }
};

export const useFetchChatMessagesQuery = (
  timezone: string,
  appointmentId?: string,
  onSuccess?: (data: ConversationMessage[] | null) => void
): UseQueryResult<ConversationMessage[] | null, Error> => {
  const { oystehrZambda } = useApiClients();

  const queryResult = useQuery({
    queryKey: ['chat-messages', appointmentId, timezone],

    queryFn: async () => {
      if (!oystehrZambda || !appointmentId) {
        return null;
      }

      const response = await oystehrZambda.zambda.execute({
        id: 'get-chat-messages',
        appointmentId,
        timezone,
      });
      const data = response.output as ConversationMessage[] | null;
      return data ?? null;
    },

    enabled: Boolean(oystehrZambda && appointmentId && timezone),
  });

  useSuccessQuery(queryResult.data, onSuccess);

  return queryResult;
};

export interface ConversationTokenData {
  token: string;
  conversationSid: string;
}

export const useConversationTokenQuery = (
  appointmentId?: string
): UseQueryResult<ConversationTokenData | null, Error> => {
  const { oystehrZambda } = useApiClients();

  return useQuery({
    queryKey: ['conversation-token', appointmentId],
    queryFn: async () => {
      if (!oystehrZambda || !appointmentId) return null;
      const response = await oystehrZambda.zambda.execute({
        id: 'get-conversation-token',
        appointmentId,
      });
      return response.output as ConversationTokenData;
    },
    enabled: Boolean(oystehrZambda && appointmentId),
    staleTime: 50 * 60 * 1000,
    gcTime: 55 * 60 * 1000,
  });
};

export const useSendMessagesMutation = (
  appointmentId: string,
  message: string,
  onSuccess: (data: MessageModel) => void,
  onError: (error: Error) => void
): UseMutationResult<MessageModel, Error, MessageModel> => {
  const { oystehrZambda } = useApiClients();

  return useMutation({
    mutationKey: ['chat-messages'],

    mutationFn: async (pendingMessage: MessageModel): Promise<MessageModel> => {
      if (oystehrZambda === undefined) {
        throw new Error(`Message send failed. OystehrZambdaUndefined`);
      }
      let sendResponse;
      try {
        sendResponse = await oystehrZambda.zambda.execute({
          id: 'send-chat-message',
          appointmentId,
          message,
        });
      } catch (error) {
        throw new Error(`Message send failed: ${normalizeErrorMessage(error)}`);
      }
      const responseOutput = sendResponse.output as { messageId?: string; timestamp?: string } | undefined;
      const messageId = responseOutput?.messageId || responseOutput?.timestamp || pendingMessage.id;
      return { ...pendingMessage, resolvedId: messageId, id: messageId };
    },

    onSuccess,
    onError,
  });
};

export const useGetMessagingConfigQuery = (
  onSuccess?: (data: MessagingGetMessagingConfigResponse | null) => void,
  onError?: () => void
): UseQueryResult<MessagingGetMessagingConfigResponse, Error> => {
  const { oystehr } = useApiClients();

  const queryResult = useQuery({
    queryKey: ['messaging-config'],

    queryFn: async () => {
      if (!oystehr) {
        throw new Error('API client not available');
      }
      return await oystehr.messaging.getMessagingConfig();
    },

    enabled: !!oystehr,
  });

  useSuccessQuery(queryResult.data, onSuccess);

  useErrorQuery(queryResult.error, onError);

  return queryResult;
};

export const useGetQuickTextsQuery = (
  onSuccess?: (data: QuickText[] | null) => void
): UseQueryResult<QuickText[], Error> => {
  const { oystehrZambda } = useApiClients();

  const queryResult = useQuery({
    queryKey: ['quick-texts'],

    queryFn: async () => {
      if (!oystehrZambda) {
        throw new Error('API client not available');
      }
      const { getQuickTexts } = await import('../../api/api');
      return await getQuickTexts(oystehrZambda);
    },

    enabled: !!oystehrZambda,
  });

  useSuccessQuery(queryResult.data, onSuccess);

  return queryResult;
};

export const useDeleteQuickTextMutation = (
  onSuccess?: () => void,
  onError?: (error: Error) => void
): UseMutationResult<void, Error, string> => {
  const { oystehrZambda } = useApiClients();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (quickTextId: string): Promise<void> => {
      if (!oystehrZambda) {
        throw new Error('API client not available');
      }
      const { deleteQuickText } = await import('../../api/api');
      return await deleteQuickText(oystehrZambda, quickTextId);
    },

    onSuccess: () => {
      // Invalidate quick-texts query to refetch the list
      void queryClient.invalidateQueries({ queryKey: ['quick-texts'] });
      onSuccess?.();
    },

    onError,
  });
};

export const useCreateQuickTextMutation = (
  onSuccess?: (data: QuickText) => void,
  onError?: (error: Error) => void
): UseMutationResult<QuickText, Error, string> => {
  const { oystehrZambda } = useApiClients();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (text: string): Promise<QuickText> => {
      if (!oystehrZambda) {
        throw new Error('API client not available');
      }
      const { createQuickText } = await import('../../api/api');
      return await createQuickText(oystehrZambda, text);
    },

    onSuccess: (data) => {
      // Invalidate quick-texts query to refetch the list
      void queryClient.invalidateQueries({ queryKey: ['quick-texts'] });
      onSuccess?.(data);
    },

    onError,
  });
};

export const usePatchQuickTextMutation = (
  onSuccess?: (data: QuickText) => void,
  onError?: (error: Error) => void
): UseMutationResult<QuickText, Error, { id: string; text: string }> => {
  const { oystehrZambda } = useApiClients();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string }): Promise<QuickText> => {
      if (!oystehrZambda) {
        throw new Error('API client not available');
      }
      const { patchQuickText } = await import('../../api/api');
      return await patchQuickText(oystehrZambda, id, text);
    },

    onSuccess: (data) => {
      // Invalidate quick-texts query to refetch the list
      void queryClient.invalidateQueries({ queryKey: ['quick-texts'] });
      onSuccess?.(data);
    },

    onError,
  });
};

export const useUnreadMessagesQuery = (
  practitionerId?: string,
  limit = 6
): UseQueryResult<UnreadMessagesOutput, Error> => {
  const { oystehrZambda } = useApiClients();

  return useQuery({
    queryKey: ['unread-messages', practitionerId, limit],
    queryFn: async () => {
      if (!oystehrZambda || !practitionerId) {
        throw new Error('API client or practitionerId not available');
      }
      const { getUnreadMessages } = await import('../../api/api');
      return await getUnreadMessages(oystehrZambda, {
        limit,
        type: 'patient-message',
        practitioner: practitionerId,
      });
    },
    enabled: !!(oystehrZambda && practitionerId),
    staleTime: 60000, // 1 minute
  });
};
